
import { Component, Mixins } from "vue-property-decorator";
import NavHeader from "@/components/NavHeader.vue";
import PaginatedSearch from "@/mixins/PaginatedSearch";
import NotificationBrandingTemplatePagedRequest from "@/types/sv-data/notification-templates/NotificationTemplatePagedRequest";
import NotificationTemplateApi from "@/services/api.notificationTemplate.service";
import { PaginatedSearchQueryParams, UserPermissions } from "@/store/types";
import { Getter, namespace } from "vuex-class";
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
import RichTextEditor from "@/components/form/RichTextEditor.vue";
import RichTextEditorPlaceholder from "@/types/RichTextEditorPlaceholder";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";
import { truncateString } from "@/filters";
import CustomFieldTableView from "@/components/custom-fields/CustomFieldModalTableView.vue";
import { CustomFieldDto, CustomFieldTableTypes } from "@/store/custom-fields/types";
import { ModalItem } from "../table/generic-update-modal.vue";
import NotificationBrandingTemplate from "@/types/sv-data/notification-templates/NotificationBrandingTemplate";
import { helpers } from 'vuelidate/lib/validators';

const CustomFields = namespace("customFields");

@Component({
	filters: { truncateString },
	components: {
		"nav-header": NavHeader,
		"generic-table": GenericTable,
		"custom-field-modal-table-view": CustomFieldTableView,
	},
})
export default class NotificationBrandingTemplatesetup extends Mixins(PaginatedSearch) {
	$refs!: {
		customFieldTableView: CustomFieldTableView;
	};

	/**
	 * User permissions from Vuex.
	 */
	@Getter("getPermissions") private permissions!: UserPermissions;
	/**
	 * Feature getter from Vuex.
	 */
	@Getter("getFeature") getFeature!: (featureName: string[]) => boolean;

	/**
	 * Notification template custom fields from Vuex.
	 */
	@CustomFields.State private notificationTemplateCustomFields!: CustomFieldDto[];
	/**
	 * Action to retrieve custom fields from Vuex.
	 */
	@CustomFields.Action retrieveCustomFields!: ({ tableType, live, skipCancel }: { tableType: number, live: boolean, skipCancel: boolean }) => Promise<void>;

	/**
	 * Controls whether the modal can close on save.
	 */
	private canCloseOnSave: boolean = true;
	/**
	 * Stores custom field placeholders for the editor.
	 */
	private customFields: RichTextEditorPlaceholder[] = [];
	/**
	 * Loading state for the table.
	 */
	private isLoading: boolean = false;
	/**
	 * Stores manual fields for the editor.
	 */
	private manualFields: RichTextEditorPlaceholder[] = [];
	/**
	 * List of notification branding templates.
	 */
	private notificationBrandingTemplates: NotificationBrandingTemplate[] = [];
	/**
	 * Table type for custom fields.
	 */
	private readonly customFieldTableType: number = CustomFieldTableTypes.NotificationTemplate;
	/**
	 * Currently selected notification branding template.
	 */
	private selectedNotificationBrandingTemplate: NotificationBrandingTemplate | null = null;
	/**
	 * Total number of records for pagination.
	 */
	private totalRecords: number = 0;

	/**
	 * Lifecycle hook: Called after the component is mounted.
	 * Loads notification branding templates and custom fields if enabled.
	 */
	private async mounted(): Promise<void> {
		await this.getPaginatedNotificationBrandingTemplates();

		if (this.isCustomFieldsNotificationTemplatesEnabled) {
			await this.retrieveCustomFields({
				tableType: CustomFieldTableTypes.NotificationTemplate,
				live: true,
				skipCancel: false,
			});
		}
	}

	/**
	 * Returns true if notification templates feature is enabled.
	 */
	private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

	/**
	 * Returns true if custom fields for notification templates are enabled.
	 */
	private get isCustomFieldsNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["CustomFields", "NotificationTemplates"]);
	}

	/**
	 * Returns true if the user has permission to view or edit notification templates.
	 */
	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}

	/**
	 * Returns true if the user has permission to view custom fields.
	 */
	private get hasCanViewCustomFieldsPermission(): boolean {
		return this.permissions.canViewCustomFields;
	}

	/**
	 * Returns true if branding templates feature is enabled.
	 */
	private get isBrandingTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "BrandingTemplates"]);
	}

	/**
	 * Creates a new notification branding template if the required placeholder is present.
	 * @param notificationTemplate The notification branding template to create.
	 */
	private async createNotificationBrandingTemplate(notificationTemplate: NotificationBrandingTemplate): Promise<void> {
		if (this.isContentPlaceholderRequired(notificationTemplate)) {
			await NotificationTemplateApi.createNotificationBrandingTemplate(notificationTemplate);
			await this.getPaginatedNotificationBrandingTemplates(this.mostRecentSearchParams);
		}
	}

	/**
	 * Retrieves paginated notification branding templates.
	 * @param paginatedSearchQueryParams Optional search params for pagination.
	 */
	private async getPaginatedNotificationBrandingTemplates(
		paginatedSearchQueryParams?: PaginatedSearchQueryParams
	): Promise<void> {
		this.isLoading = true;
		this.notificationBrandingTemplates = [];

		if (!paginatedSearchQueryParams) {
			paginatedSearchQueryParams = {
				page: 1,
				pageSize: 25,
				sortDesc: false,
				sortBy: "Title",
				searchTerm: "",
			};
		}

		const searchParams = this.generateNewPaginatedSearchRequest(paginatedSearchQueryParams);

		const request = { ...searchParams, ...paginatedSearchQueryParams } as NotificationBrandingTemplatePagedRequest;

		// Hard coded as no other types exist
		request.notificationTemplateTypeId = NotificationTemplateTypeId.Event;

		this.mostRecentSearchParams = request;

		const results = await NotificationTemplateApi.getNotificationBrandingTemplates(request, request.groupId);
		this.totalRecords = results.totalRecords;
		this.notificationBrandingTemplates = results.data;

		this.notificationBrandingTemplates.forEach((template: NotificationBrandingTemplate) => {
			// set the hideDelete property so we can disable the delete icon if the template has linked templates and use a custom icon explaining why
			template.hideDelete = template.linkedTemplates.length > 0;
		});

		this.isLoading = false;
	}

	/**
	 * Updates a notification template if the required placeholder is present.
	 * @param notificationTemplate The notification template to update.
	 */
	private async updateNotificationTemplate(notificationTemplate: NotificationBrandingTemplate): Promise<void> {
		if (this.isContentPlaceholderRequired(notificationTemplate)) {
			await NotificationTemplateApi.updateNotificationTemplate(notificationTemplate);
			await this.getPaginatedNotificationBrandingTemplates(this.mostRecentSearchParams);
		}
	}

	/**
	 * Deletes a notification template and refreshes the list.
	 * @param notificationTemplate The notification template to delete.
	 */
	private async deleteNotificationTemplate(notificationTemplate: NotificationBrandingTemplate): Promise<void> {
		await NotificationTemplateApi.deleteNotificationTemplate(notificationTemplate.notificationTemplateId);
		await this.getPaginatedNotificationBrandingTemplates(this.mostRecentSearchParams);
	}

	/**
	 * Handles modal open event, sets the selected template and manual fields.
	 * @param value The selected notification branding template or null.
	 */
	private async onModalOpen(value: NotificationBrandingTemplate | null): Promise<void> {
		if (value) {
			this.selectedNotificationBrandingTemplate = value;
			this.manualFields = this.selectedNotificationBrandingTemplate.manualFields;
		} else {
			this.selectedNotificationBrandingTemplate = null;
			this.manualFields = [];
		}
	}

	/**
	 * Opens the custom field modal dialog.
	 */
	private async openCustomFieldModal(): Promise<void> {
		this.$refs.customFieldTableView.showCustomFieldModal();
	}

	/**
	 * Returns the custom field placeholders for the template.
	 */
	private get getCustomFieldPlaceholders(): RichTextEditorPlaceholder[] {
		this.customFields = [];
		if (
			!this.isCustomFieldsNotificationTemplatesEnabled ||
			!this.hasCanViewCustomFieldsPermission ||
			(this.selectedNotificationBrandingTemplate && !this.selectedNotificationBrandingTemplate.customFields)
		) {
			return this.customFields;
		}
		if (Array.isArray(this.notificationTemplateCustomFields)) {
			this.notificationTemplateCustomFields.forEach((cf: CustomFieldDto) => {
				this.customFields.push({
					id: cf.id,
					label: cf.title,
				});
			});
		}
		return this.customFields;
	}

	/**
	 * Returns the column definitions for the generic table.
	 */
	private get columns(): TableHeader[] {
		const _columns: TableHeader[] = [
			{
				title: "Title",
				key: "title",
				order: 1,
				dataType: "input",
				description: "Notification Template Title",
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				isTermLabel: true,
			},
			{
				title: "Linked Templates",
				key: "linkedTemplates",
				order: 4,
				dataType: "text",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				useCustomCell: true,
			},
		];

		return _columns;
	}

	/**
	 * Returns the available placeholders for the template body.
	 */
	private get placeholders(): RichTextEditorPlaceholder[] {
		const placeholderItems: RichTextEditorPlaceholder[] = [
			{
				id: "areaName",
				label: "Affected Area",
			},
			{
				id: "areaAddress",
				label: "Area Address",
			},
			{
				id: "areaReferenceId",
				label: "Area Unique Identifier",
			},
			{
				id: "eventCreatedDate",
				label: "Event Started",
			},
			{
				id: "alarms",
				label: "Alarms",
			},
			{
				id: "eventOutcome",
				label: "Event Outcome",
			},
			{
				id: "eventOutcomeNote",
				label: "Event Outcome Note",
			},
			{
				id: "note",
				label: "Note",
			},
			{
				id: "senderName",
				label: "Sender Name",
			},
			{
				id: "eventUsers",
				label: "Event Users",
			},
			{
				id: "eventId",
				label: "Event ID",
			},
			{
				id: "emailContent",
				label: "Email Content",
			},
		];

		return placeholderItems.sort((a, b) => a.label.localeCompare(b.label));
	}

	/**
	 * Checks if the emailContent placeholder is required and present in the template body.
	 * Notifies the user if missing.
	 * @param value The notification branding template to check.
	 * @returns True if the placeholder is present, false otherwise.
	 */
	private isContentPlaceholderRequired(value: NotificationBrandingTemplate): boolean {
		// Check if the body contains the emailContent placeholder when the template type is "Layout"
		const placeholderFound = this.templateHasEmailContentPlaceholder(value);

		if (!placeholderFound) {
			this.$notify({
				type: "error",
				title: "Email Content Placeholder Required",
				text: "The Email Content placeholder is required in the body of the branding template.",
			});
		}

		return placeholderFound;
	}

	/**
	 * Checks if the template body contains the emailContent placeholder.
	 * @param template The notification branding template to check.
	 * @returns True if the placeholder is present, false otherwise.
	 */
	private templateHasEmailContentPlaceholder(template: NotificationBrandingTemplate): boolean {
		if (!template || !template.body) {
			return false;
		}
		// Check if the body contains the emailContent placeholder
		return template.body.toString().includes("emailContent");
	}

	/**
	 * Returns the modal item definitions for the generic table modal.
	 */
	private get modalItems(): ModalItem[] {
		return [
			{
				title: "Title",
				key: "title",
				dataType: "text",
				required: true,
				maxLength: 50,
				description: "Enter a branding template title",
				placeholder: "Notification template title",
			},
			{
				title: "Body",
				key: "body",
				dataType: "component",
				data: RichTextEditor,
				props: {
					placeholders: this.placeholders,
					manualFields: this.manualFields,
					minHeight: "350px",
					customFields: this.getCustomFieldPlaceholders,
				},
				required: true,
				validations: {
					DoesBodyContainEmailContentPlaceholder: helpers.withParams(
						{
							type: "DoesBodyContainEmailContentPlaceholder", errorMessage: `Layout Must contain the Email Content placeholder` },
						value => {
							const tempObj = {
								body: value,
							} as NotificationBrandingTemplate;

							return this.templateHasEmailContentPlaceholder(tempObj);
						}
					)
				},
			},
		];
	}

	/**
	 * Returns the delete action messages for a given template.
	 * @param row The notification branding template to delete.
	 * @returns Array of warning messages.
	 */
	private deleteActionMessages(row: NotificationBrandingTemplate): string[] {
		const deleteActionMessagesArr: string[] = [];

		deleteActionMessagesArr.push(
			"Will cause significant issues with any templates that are still using it. Removing it will result in errors and a broken appearance for any templates that rely on it, potentially disrupting your communications. Please ensure no templates are using this template before deleting it."
		);

		return deleteActionMessagesArr;
	}
}
